import { apyToAprPercent } from '../../../utils/parsing'
import { YieldFetcher } from '../../types'

/**
 * Inverse Finance sDOLA — ERC-4626 auto-compounder over the DOLA
 * Savings Account. Yield is FiRM's DBR revenue, converted to DOLA by a
 * continuous on-chain auction and accrued into the share price — no
 * rehypothecation, the DOLA never leaves the contract. Instant
 * permissionless wrap/unwrap (verified on-chain 2026-08:
 * `maxDeposit = uint256.max`, docs confirm no cooldown/fee).
 *
 * Rate source: Inverse's own API. `/api/dola-staking` is the dedicated
 * endpoint (`calculatedApy`, percent APY); the junior-tranche endpoint
 * `/api/junior/jdola-staking` duplicates it as `sDolaApy` and serves
 * as the fallback. Both are converted to the package-wide nominal APR.
 *
 * jDOLA (the junior tranche on the same endpoint) is deliberately NOT
 * integrated: ~$28k TVL, 48h exit window plus a withdraw fee — revisit
 * if it grows.
 *
 * sDOLA is Ethereum-only in our token lists (group `sDOLA::SDOLA`);
 * DOLA itself is multichain and Inverse propagates the sDOLA exchange
 * rate cross-chain via CCIP, so bridged deployments may appear later.
 */

const DOLA_STAKING_URL = 'https://www.inverse.finance/api/dola-staking'
const JDOLA_STAKING_URL = 'https://www.inverse.finance/api/junior/jdola-staking'

export const SDOLA_KEY = 'SDOLA'
/** Verified token-list assetGroup (Ethereum, the only deployment). */
export const SDOLA_GROUP_KEY = 'sDOLA::SDOLA'

const fetchJson = async (url: string): Promise<any> => {
  const res = await fetch(url, {
    headers: { accept: 'application/json' },
    signal: AbortSignal.timeout(8000),
  })
  if (!res.ok) throw new Error(`HTTP ${res.status}`)
  return res.json()
}

const fetchSdolaApyPercent = async (): Promise<number> => {
  try {
    const d = await fetchJson(DOLA_STAKING_URL)
    const apy = Number(d?.calculatedApy)
    if (Number.isFinite(apy) && apy > 0) return apy
    throw new Error('calculatedApy missing')
  } catch {
    const d = await fetchJson(JDOLA_STAKING_URL)
    const apy = Number(d?.sDolaApy)
    if (Number.isFinite(apy) && apy > 0) return apy
    throw new Error('sDolaApy missing')
  }
}

export const sdolaFetcher: YieldFetcher = {
  label: 'SDOLA',
  fetch: async () => {
    const apr = apyToAprPercent(await fetchSdolaApyPercent())
    return { [SDOLA_KEY]: apr, [SDOLA_GROUP_KEY]: apr }
  },
}
